import * as appSelectors from 'app/state/app/app-selectors';
import {
  DASHBOARD_REPORTING_HISTORY_CONFIG_PATH,
  DISABLE_PERMIT_REPORTING_HISTORY_PATH,
  LAST_12_MONTHS_OPTION,
} from 'common/constants';
import { ReportingHistory } from 'deckard-ui/dist/components/ReportingHistory/reporting-history.js';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  filterByLast12Months,
  filterBySameYear,
} from 'utils/utility-functions';

import { ReportingHistoryFilter } from './reporting-history-filter';
import { ReportingHistoryLoader } from './reporting-history-loader';
import { Container } from './styles';

/**
 * This component handles rendering of the tax reporting history for the individual permit.
 * It leverages the "ReportingHistory" component from our internal component library (lib-deckardui).
 *
 * The data comes from "/reportingHistoryByApplicationNumber" API. No data parsing occurs here as
 * the "ReportingHistory" component will handle it for us.
 *
 * Configuration of how the data is shown to the user is dictated by the client JSON.
 * For an example, see "reporting-history-config.ts" from
 * "scripts/stats-src/places/tx-galveston-city_of_galveston/portal/reporting-history-config.ts"
 */
export function SinglePermitReportingHistory(props) {
  const {
    paymentPeriods,
    fetchInflight,
    failedLicenseFetch,
    fetchLicenseData,
  } = props;

  const [selectedYearOption, setSelectedYearOption] = useState(
    LAST_12_MONTHS_OPTION,
  );

  const hideDashboardPermitsDisplay = useSelector(
    appSelectors.getFromPortalStats(DISABLE_PERMIT_REPORTING_HISTORY_PATH),
  );

  const reportingHistoryConfig = useSelector(
    appSelectors.getFromPortalStats(DASHBOARD_REPORTING_HISTORY_CONFIG_PATH),
  );

  const filteredReportingHistory = useMemo(() => {
    if (!paymentPeriods) return null;

    const filterFn =
      selectedYearOption === LAST_12_MONTHS_OPTION
        ? filterByLast12Months
        : filterBySameYear;

    return paymentPeriods.filter((period) =>
      filterFn(period, selectedYearOption),
    );
  }, [paymentPeriods, selectedYearOption]);

  if (hideDashboardPermitsDisplay || isNil(reportingHistoryConfig)) return null;

  return (
    // Additional styling of "ReportingHistory" components occurs in "Container"
    <Container>
      <ReportingHistoryFilter
        data={paymentPeriods}
        selectedYearOption={selectedYearOption}
        setSelectedYearOption={setSelectedYearOption}
      />

      {isEmpty(filteredReportingHistory) ? (
        <ReportingHistoryLoader
          disabled={fetchInflight}
          onClick={fetchLicenseData}
          dataFetchHasFailed={failedLicenseFetch}
        />
      ) : (
        <ReportingHistory
          data={filteredReportingHistory}
          {...reportingHistoryConfig}
        />
      )}
    </Container>
  );
}
